import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface StatsSectionProps {
  className?: string;
}

/**
 * StatsSection - Key statistics section
 * 
 * Features:
 * - Displays key statistics with large numbers
 * - Responsive design for mobile and desktop
 * - Clean typography and spacing
 * 
 * @returns {JSX.Element} The stats section component
 */
export const StatsSection: React.FC<StatsSectionProps> = ({ className }) => {
  const { t } = useTranslation('marketing');

  return (
    <section
      className={`w-full bg-white ${className || ''}`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-20">
        <div className="flex flex-col items-center justify-center py-10 sm:py-14 lg:py-20">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-dark-invirtus/70 text-base sm:text-lg lg:text-xl font-medium uppercase tracking-[0.2em] text-center mb-6 sm:mb-8 lg:mb-10 font-body"
          >
            {t('stats.eyebrow')}
          </motion.h2>

          {/* Stats Grid — 1 column on mobile, 3 columns from sm+ so tablets
              get the side-by-side layout, not just desktops. */}
          <div className="w-full max-w-4xl">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 justify-items-center">
              {/* Stat 1 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex flex-col items-center text-center"
              >
                <div className="text-dark-invirtus text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  {t('stats.cost_value')}
                </div>
                <div className="text-dark-invirtus/70 text-base sm:text-lg lg:text-xl font-normal leading-relaxed">
                  {t('stats.cost_label')}
                </div>
              </motion.div>

              {/* Stat 2 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex flex-col items-center text-center"
              >
                <div className="text-dark-invirtus text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  {t('stats.speed_value')}
                </div>
                <div className="text-dark-invirtus/70 text-base sm:text-lg lg:text-xl font-normal leading-relaxed">
                  {t('stats.speed_label')}
                </div>
              </motion.div>

              {/* Stat 3 */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.05 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="flex flex-col items-center text-center"
              >
                <div className="text-dark-invirtus text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  {t('stats.hires_value')}
                </div>
                <div className="text-dark-invirtus/70 text-base sm:text-lg lg:text-xl font-normal leading-relaxed">
                  {t('stats.hires_label')}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom border */}
      <motion.div
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 0.05 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="w-[80%] mx-auto h-0.5 bg-[#E1D2DA]"
      />
    </section>
  );
};
